import  normalize from 'json-api-normalize';
import { FieldModel, LabelValueModel, SpecialtyModel } from '../models';
import { of } from 'rxjs';
import { TrainingInfoModel } from '../models/training-info.model';
import { formatPercent } from '@angular/common';
import { MediaPathHelper} from './media-path.helper';

export class SpecialtyServiceHelper {
  public static parseFeaturedSpecialties(featuredJsonObj: object) {
    const items = normalize(featuredJsonObj).get('items');

    if (Array.isArray(items) && items[0] && items[0].dataset && items[0].dataset.data) {
      return items[0].dataset.data.map(featuredSpecialty => {
        if (!featuredSpecialty || !featuredSpecialty.attributes) {
          return;
        }
        // get the img data from the includes on the parent object
        const imageField = featuredSpecialty.relationships.field_image;
        const specialtyImages = MediaPathHelper.getMedia(imageField, featuredJsonObj['included']);

        return new SpecialtyModel({
          uuid: featuredSpecialty.id,
          name: featuredSpecialty.attributes.title,
          nid: featuredSpecialty.attributes.drupal_internal__nid,
          type: featuredSpecialty.attributes.field_specialty_designation,
          path: featuredSpecialty.attributes.path ? featuredSpecialty.attributes.path : '',
          description: featuredSpecialty.attributes.body ? featuredSpecialty.attributes.body.processed : '',
          definingFeatures: featuredSpecialty.attributes.field_defining_features ? featuredSpecialty.attributes.field_defining_features : [],
          imageUrl: specialtyImages ? specialtyImages['hero_desktop']['href'] : '',
        });
      });
    }
  }

  public static parseSpecialty(specialtyJson: string, specialtyFieldModels: FieldModel[]) {
    const specialtyFields = [
      'id',
      'title',
      'drupal_internal__nid',
      'field_association',
      'field_association_logo',
      'field_specialty_designation',
      'path',
      'body',
      'field_browse_programs.title',
      'field_browse_programs.field_program_id',
      'field_specialty_overview',
      'field_image',
      'metatag_normalized'
    ];

    const normalizedSpecialty = normalize(specialtyJson).get(specialtyFields);
    const specialtyJsonString = JSON.stringify(specialtyJson);
    const mediaImages = MediaPathHelper.getNormalizedMedia(normalizedSpecialty.field_image, specialtyJsonString);
    const associationLogo = MediaPathHelper.getNormalizedMedia(normalizedSpecialty.field_association_logo, specialtyJsonString);

    return new SpecialtyModel({
      uuid: normalizedSpecialty.id,
      name: normalizedSpecialty.title,
      nid: normalizedSpecialty.drupal_internal__nid,
      type: normalizedSpecialty.field_specialty_designation,
      path: normalizedSpecialty.path ? normalizedSpecialty.path : '',
      imageUrl: mediaImages ? mediaImages.hero_desktop.href : null,
      associations: normalizedSpecialty.field_association ? normalizedSpecialty.field_association.processed : '',
      association_logo: associationLogo? associationLogo: '',
      description: normalizedSpecialty.body ? normalizedSpecialty.body.processed : '',
      trainingInfoModel: this.parseTrainingModel(specialtyJson, specialtyFieldModels),
      relatedPrograms: normalizedSpecialty.field_browse_programs,
      overview: normalizedSpecialty.field_specialty_overview ? normalizedSpecialty.field_specialty_overview.processed : '',
      metatags: normalizedSpecialty.metatag_normalized ? normalizedSpecialty.metatag_normalized : null
    });
  }

  public static parseSpecialties(specialtiesJson: string) {
    const specialtyFields = [
      'id',
      'title',
      'drupal_internal__nid',
      'field_specialty_designation',
      'path',
      'field_details',
      'body',
      'metatag_normalized'
    ];

    const normalizedSpecialties = normalize(specialtiesJson).get(specialtyFields);
    const specialtyResults = Array.isArray(normalizedSpecialties) ?
      normalizedSpecialties.map((specialty: any) => new SpecialtyModel({
        uuid: specialty.id,
        name: specialty.title,
        nid: specialty.drupal_internal__nid,
        type: specialty.field_specialty_designation,
        path: specialty.path ? specialty.path : '',
        description: specialty.body,
        link: specialty.path ? specialty.path : '',
        metatags: specialty.metatag ? specialty.metatag : ''
      }))
      : [ new SpecialtyModel({
        uuid: normalizedSpecialties.id,
        name: normalizedSpecialties.title,
        nid: normalizedSpecialties.drupal_internal__nid,
        type: normalizedSpecialties.field_specialty_designation,
        path: normalizedSpecialties.path,
        description: normalizedSpecialties.body,
        link: normalizedSpecialties.path ? normalizedSpecialties.path : '',
        metatags: normalizedSpecialties.metatag ? normalizedSpecialties.metatag : null
      }) ];
    return of(specialtyResults);
  }

  public static parseSubSpecialties(specialtiesJson: {}) {
    return normalize(specialtiesJson).get(['title', 'path']);
  }

  public static parseTrainingModel(specialtyJson: string, specialtyFieldModels: FieldModel[]) {
    const specialtyFields = [
      'field_academic_year',
      'field_academician',
      'field_academician_full',
      'field_average_percent_female',
      'field_average_vacation',
      'field_avg_days_off_duty_per_week',
      'field_avg_hours_on_duty_per_week',
      'field_avg_max_consecutive_hours',
      'field_avg_no_full_time_physician',
      'field_avg_no_part_time_physician',
      'field_avg_number_resident_fellow',
      'field_avg_pct_dos',
      'field_avg_pct_female_full_time',
      'field_avg_pct_intl_med_grad',
      'field_avg_pct_train_ambulatory',
      'field_avg_pct_train_outpatient',
      'field_avg_py1_interviews',
      'field_avg_ratio_phys_res_fellow',
      'field_avg_res_fell_compensation',
      'field_completing_training',
      'field_group_practice',
      'field_length_accredited_training',
      'field_military',
      'field_min_prior_years_required',
      'field_nhsc_underserved',
      'field_non_clinical_research',
      'field_non_medical_career',
      'field_number_accredited_programs',
      'field_offers_gy1_positions_immed',
      'field_pct_programs_req_l1',
      'field_pct_programs_req_s1',
      'field_practicing_in_the_us',
      'field_pursuing_more_training',
      'field_related_training',
      'field_same_specialty',
      'field_same_state',
      'field_total_no_active_res_fellow',
      'field_unemployed',
      'field_with_known_plans',
      'field_defining_features',
      'field_grad_year',
      'field_specialty_overview'
    ];

    const normalizedSpecialty = normalize(specialtyJson).get(specialtyFields);
    const processedSpecialty = {};
    const getFormattedPercent = (numerator, denominator) => {
      if (numerator === null || numerator === undefined || denominator === null || denominator === undefined) {
        return 'N/A';
      }

      return `${ numerator } (${ formatPercent(numerator / denominator, 'en', '1.0-1') })`;
    };

    // Process normalized specialty values
    Object.keys(normalizedSpecialty).forEach(key => {
      const value = normalizedSpecialty[key];

      // this switch contains specific processors for certain keys
      switch (key) {
      case 'field_with_known_plans':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_completing_training']);
      case 'field_pursuing_more_training':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_with_known_plans']);
      case 'field_practicing_in_the_us':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_with_known_plans']);
      case 'field_group_practice':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_practicing_in_the_us']);
      case 'field_same_specialty':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_practicing_in_the_us']);
      case 'field_same_state':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_practicing_in_the_us']);
      case 'field_nhsc_underserved':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_practicing_in_the_us']);
      case 'field_academician':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_with_known_plans']);
      case 'field_academician_full':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_academician']);
      case 'field_military':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_with_known_plans']);
      case 'field_non_clinical_research':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_with_known_plans']);
      case 'field_non_medical_career':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_with_known_plans']);
      case 'field_unemployed':
        return processedSpecialty[key] = getFormattedPercent(value, normalizedSpecialty['field_with_known_plans']);
      case 'field_avg_res_fell_compensation': {
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        });

        return processedSpecialty[key] = formatter.format(value); /* $2,500.00 */
      }
      case 'field_related_training': {
        return processedSpecialty[key] = value; // don't process this one
      }
      case 'field_grad_year': {
        return processedSpecialty[key] = value; // don't process this one
      }
      case 'field_academic_year': {
        return processedSpecialty[key] = value; // don't process this one
      }
      }

      // this processor change values into percentages if the key contains the _pct_ keyword
      if (key.toLowerCase().indexOf('_pct_') !== -1 || key.toLowerCase().indexOf('_percent_') !== -1) {
        return processedSpecialty[key] = `${ formatPercent(value / 100, 'en') }`;
      }

      // this processor ensures that all numbers are formatted with commas
      if (Number(value)) {
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'decimal',
        });

        return processedSpecialty[key] = formatter.format(value);
      }

      if (value === null || value === undefined) {
        return processedSpecialty[key] = 'N/A';
      }

      // return the unprocessed value if there are no modifications
      return processedSpecialty[key] = value;
    });

    // generates a list of LabelValueModels to be used for table data
    const labelValues = {};
    Object.keys(processedSpecialty).forEach(key => {
      if (!specialtyFieldModels[key]) {
        return;
      }

      labelValues[key] = new LabelValueModel({
        field: specialtyFieldModels[key].field,
        label: specialtyFieldModels[key].label,
        value: processedSpecialty[key] ? processedSpecialty[key] : 'N/A'
      });
    });

    return new TrainingInfoModel({
      // General Program Info
      academicYear: labelValues['field_academic_year'],
      numberAccreditedPrograms: labelValues['field_number_accredited_programs'],
      lengthAccreditedTraining: labelValues['field_length_accredited_training'],
      minNumberPriorYearsRequired: labelValues['field_min_prior_years_required'],
      offersGraduateYearOnePositions: labelValues['field_offers_gy1_positions_immed'],
      avgNumberProgramYearOneInterviews: labelValues['field_avg_py1_interviews'],
      percentRequiringStepOneScore: labelValues['field_pct_programs_req_s1'],
      percentRequiringLevelOneScore: labelValues['field_pct_programs_req_l1'],
      // Residents
      totalActiveResidentsOrFellows: labelValues['field_total_no_active_res_fellow'],
      avgResidentsOrFellows: labelValues['field_avg_number_resident_fellow'],
      avgPercentFemales: labelValues['field_average_percent_female'],
      avgPercentInternationalGraduates: labelValues['field_avg_pct_intl_med_grad'],
      avgPercentDO: labelValues['field_avg_pct_dos'],
      // Faculty
      avgFullTimePhysicians: labelValues['field_avg_no_full_time_physician'],
      avgPartTimePhysicians: labelValues['field_avg_no_part_time_physician'],
      avgPercentFemaleFullTimePhysician: labelValues['field_avg_pct_female_full_time'],
      avgRatioFullTimeToResidentToFellow: labelValues['field_avg_ratio_phys_res_fellow'],
      // Resident Work hours (PY1)
      avgHoursOnDutyPerWeek: labelValues['field_avg_hours_on_duty_per_week'],
      avgMaxConsecutiveHoursOnDuty: labelValues['field_avg_max_consecutive_hours'],
      avgDaysOffPerWeek: labelValues['field_avg_days_off_duty_per_week'],
      // Work Environment And Compensation
      avgPercentTrainingInHospitalOutpatientClinics: labelValues['field_avg_pct_train_outpatient'],
      avgPercentTrainingInNonHospitalAmbulatoryCare: labelValues['field_avg_pct_train_ambulatory'],
      avgResidentToFellowCompensation: labelValues['field_avg_res_fell_compensation'],
      avgWeeksOfVacation: labelValues['field_average_vacation'],
      // Graduates' career plans
      graduatesCompletedTraining: labelValues['field_completing_training'],
      graduatesWithKnownPlans: labelValues['field_with_known_plans'],
      graduatesPursuingMoreTraining: labelValues['field_pursuing_more_training'],
      graduatesInPractice: labelValues['field_practicing_in_the_us'],
      graduatesInGroupPractice: labelValues['field_group_practice'],
      graduatesPracticingSameSpecialty: labelValues['field_same_specialty'],
      graduatesPracticingSameStateAsProgram: labelValues['field_same_state'],
      graduatesPracticingNHSC: labelValues['field_nhsc_underserved'],
      graduatesEnteringAcademia: labelValues['field_academician'],
      graduatesInAcademiaFullTime: labelValues['field_academician_full'],
      graduatesPursuingMilitary: labelValues['field_military'],
      graduatesPursuingNonClinicalResearch: labelValues['field_non_clinical_research'],
      graduatesPursuingNonMedicalCareer: labelValues['field_non_medical_career'],
      graduatesUnemployed: labelValues['field_unemployed'],
      graduatesGradYear: labelValues['field_grad_year'],
      relatedTraining: processedSpecialty['field_related_training'],
      // Icons
      definingFeatures: labelValues['field_defining_features'],
    });
  }
}
